import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Song } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { sections } from './lyrics';

/** Un canto: letra por secciones, tono, BPM, notas y enlaces. Alabanza lo edita aquí mismo. */
export function SongPage() {
  const { songId } = useParams();
  const me = useMe();
  const navigate = useNavigate();
  const churchId = me.activeChurchId;
  const isNew = !songId;
  const data = useAsync(
    (signal) => (churchId && songId ? api.get<Song & { canEdit: boolean }>(`/worship/churches/${churchId}/songs/${songId}`, signal) : Promise.resolve(null)),
    [churchId, songId],
  );
  const [editing, setEditing] = useState(isNew);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const s = data.data;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const body = {
      title: t('title') ?? '',
      author: t('author'),
      key: t('key'),
      bpm: t('bpm') ? Number(t('bpm')) : null,
      version: t('version'),
      lyrics: String(f.get('lyrics') ?? ''),
      notes: t('notes'),
      links: String(f.get('links') ?? '').split('\n').map((l) => l.trim()).filter(Boolean),
    };
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        const { id } = await api.post<{ id: string }>(`/worship/churches/${churchId}/songs`, body);
        navigate(`/app/cantos/${id}`, { replace: true });
      } else {
        await api.put(`/worship/churches/${churchId}/songs/${songId}`, body);
        setEditing(false);
        data.reload();
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/cantos">← Cantos</Link>
      </p>
      {editing ? (
        <>
          <h1>{isNew ? 'Nuevo canto' : 'Editar canto'}</h1>
          <form className="form" onSubmit={save} noValidate>
            {error ? <Alert>{error}</Alert> : null}
            <Field label="Título" name="title" required maxLength={150} defaultValue={s?.title} />
            <Field label="Autor (opcional)" name="author" maxLength={150} defaultValue={s?.author ?? ''} />
            <div className="form__row">
              <Field label="Tono" name="key" maxLength={12} defaultValue={s?.key ?? ''} placeholder="G, Am, D…" />
              <Field label="BPM" name="bpm" type="number" min={20} max={300} defaultValue={s?.bpm ?? ''} />
            </div>
            <Field label="Versión (opcional)" name="version" maxLength={120} defaultValue={s?.version ?? ''} placeholder="Ej. Marcos Witt, acústica, en vivo" />
            <div className="field">
              <label htmlFor="song-lyrics">Letra</label>
              <textarea id="song-lyrics" name="lyrics" rows={14} maxLength={20000} defaultValue={s?.lyrics ?? ''} placeholder={'[Estrofa 1]\nPrimera línea\nSegunda línea\n\n[Coro]\n…'} />
              <small className="field__hint">Separa cada estrofa con una línea en blanco. Puedes nombrarlas: [Coro], [Puente]… En Presentación cada estrofa es una diapositiva.</small>
            </div>
            <div className="field">
              <label htmlFor="song-notes">Notas para músicos (opcional)</label>
              <textarea id="song-notes" name="notes" rows={2} maxLength={2000} defaultValue={s?.notes ?? ''} />
            </div>
            <div className="field">
              <label htmlFor="song-links">Enlaces (uno por línea, opcional)</label>
              <textarea id="song-links" name="links" rows={2} defaultValue={s?.links.join('\n') ?? ''} placeholder="https://youtu.be/…" />
            </div>
            <div className="actions">
              <Button type="submit" busy={busy}>
                Guardar
              </Button>
              {!isNew ? (
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                  Cancelar
                </Button>
              ) : null}
            </div>
          </form>
        </>
      ) : s ? (
        <>
          <header className="page__header page__header--actions">
            <div>
              <h1>{s.title}</h1>
              <p className="muted">
                {s.author ?? 'Autor sin registrar'}
                {s.key ? ` · Tono ${s.key}` : ''}
                {s.bpm ? ` · ${s.bpm} BPM` : ''}
                {s.version ? ` · Versión: ${s.version}` : ''}
                {s.archived ? ' · Archivado' : ''}
              </p>
            </div>
            {s.canEdit ? (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Editar
              </Button>
            ) : null}
          </header>
          {error ? <Alert>{error}</Alert> : null}
          <Card>
            {s.lyrics ? (
              <div className="lyrics">
                {sections(s.lyrics).map((sec, i) => (
                  <section key={i} className={`lyrics__section${sec.name && /coro/i.test(sec.name) ? ' lyrics__section--chorus' : ''}`}>
                    {sec.name ? <h3>{sec.name}</h3> : null}
                    <p className="pre-line">{sec.lines.join('\n')}</p>
                  </section>
                ))}
              </div>
            ) : (
              <p className="muted">Sin letra todavía.</p>
            )}
          </Card>
          {s.notes || s.links.length ? (
            <Card title="Para músicos">
              {s.notes ? <p className="pre-line">{s.notes}</p> : null}
              {s.links.map((l) => (
                <a key={l} href={l} target="_blank" rel="noopener noreferrer" className="block">
                  {l}
                </a>
              ))}
            </Card>
          ) : null}
          {s.canEdit ? (
            <p>
              <Button
                variant="ghost"
                busy={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.post(`/worship/churches/${churchId}/songs/${s.id}/${s.archived ? 'restore' : 'archive'}`, {});
                    data.reload();
                  } catch (err) {
                    setError(errorMessage(err));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {s.archived ? 'Restaurar' : 'Archivar'}
              </Button>
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
